import {
  type IContractBook,
  type SupportedChainId,
  contractBook,
} from "@eulerxyz/core-sdk"
import type { ContractFunctionConfig } from "@eulerxyz/evc-sdk"
import { type Address, zeroAddress } from "viem"
import { BaseTransactionFactory } from "./BaseTransactionFactory"
import {
  type DisableCollateralTransactionItem,
  type DisableControllerTransactionItem,
  type EnableCollateralTransactionItem,
  type EnableControllerTransactionItem,
  type EvcTransaction,
  SetAccountOperatorTransactionItem,
  isDisableCollateralTransactionItem,
  isDisableControllerTransactionItem,
  isEnableCollateralTransactionItem,
  isEnableControllerTransactionItem,
  isSetAccountOperatorTransactionItem,
} from "./types"

export class EVCTransactionFactory extends BaseTransactionFactory {
  evcAddress: Address

  constructor(chainId: SupportedChainId, contractBook: IContractBook) {
    super(chainId, contractBook)
    this.evcAddress = contractBook.evc.address[chainId]
  }
  typeGuardConvertMultiple(
    _permit2Transactions: EvcTransaction[],
  ): ContractFunctionConfig[] {
    const contractCallReadyTxs: ContractFunctionConfig[] = []
    for (const tx of _permit2Transactions) {
      const call = this.typeGuardConvertSingle(tx)
      contractCallReadyTxs.push(call)
    }
    return contractCallReadyTxs
  }

  typeGuardConvertSingle(transaction: EvcTransaction): ContractFunctionConfig {
    if (isEnableCollateralTransactionItem(transaction)) {
      return this.convertEnableCollateral(transaction)
    }
    if (isEnableControllerTransactionItem(transaction)) {
      return this.convertEnableController(transaction)
    }

    if (isDisableCollateralTransactionItem(transaction)) {
      return this.convertDisableCollateral(transaction)
    }

    if (isDisableControllerTransactionItem(transaction)) {
      return this.convertDisableController(transaction)
    }

    if (isSetAccountOperatorTransactionItem(transaction)) {
      return this.convertSetAccountOperator(transaction)
    }

    throw this.errorUnsupportedTransactionType(transaction)
  }

  convertEnableCollateral(
    enableCollateral: EnableCollateralTransactionItem,
  ): ContractFunctionConfig<typeof contractBook.evc.abi, "enableCollateral"> {
    return {
      functionName: "enableCollateral",
      abi: contractBook.evc.abi,
      address: this.evcAddress,
      args: [enableCollateral.to, enableCollateral.vault],
      onBehalfOfAccount: zeroAddress,
      value: 0n,
    }
  }

  convertEnableController(
    enableController: EnableControllerTransactionItem,
  ): ContractFunctionConfig<typeof contractBook.evc.abi, "enableController"> {
    return {
      functionName: "enableController",
      abi: contractBook.evc.abi,
      address: this.evcAddress,
      args: [enableController.to, enableController.vault],
      onBehalfOfAccount: zeroAddress,
      value: 0n,
    }
  }

  convertDisableCollateral(
    disableCollateral: DisableCollateralTransactionItem,
  ): ContractFunctionConfig<typeof contractBook.evc.abi, "disableCollateral"> {
    return {
      functionName: "disableCollateral",
      abi: contractBook.evc.abi,
      address: this.evcAddress,
      args: [disableCollateral.to, disableCollateral.vault],
      onBehalfOfAccount: zeroAddress,
      value: 0n,
    }
  }

  convertDisableController(
    disableController: DisableControllerTransactionItem,
  ): ContractFunctionConfig<
    typeof contractBook.eVault.abi,
    "disableController"
  > {
    return {
      functionName: "disableController",
      abi: contractBook.eVault.abi,
      address: disableController.vault,
      onBehalfOfAccount: disableController.to,
      value: 0n,
    }
  }

  convertSetAccountOperator(
    setAccountOperator: SetAccountOperatorTransactionItem,
  ): ContractFunctionConfig<typeof contractBook.evc.abi, "setAccountOperator"> {
    return {
      functionName: "setAccountOperator",
      abi: contractBook.evc.abi,
      address: this.evcAddress,
      args: [
        setAccountOperator.account,
        setAccountOperator.operator,
        setAccountOperator.authorized,
      ],
      onBehalfOfAccount: zeroAddress,
      value: 0n,
    }
  }
}
